import * as React from "react";
import { motion, useReducedMotion } from "motion/react";
import { Loader2 } from "lucide-react";
import { ApiError } from "@eduviaos/shared";
import { Button } from "@eduviaos/ui-kit";
import type { Institution } from "../lib/institution";
import { useSession } from "../lib/sessionContext";
import { usePortalLocale } from "../lib/i18n/usePortalLocale";
import { authErrorMessage } from "../lib/apiErrorCopy";
import { InstitutionMark } from "../components/InstitutionMark";

const EASE_OUT: [number, number, number, number] = [0.23, 1, 0.32, 1];

/** Redeem a passwordless sign-in link (Phase SC-6 / SC-18). */
export function MagicLinkVerifyScreen({
  institution,
  token,
  loginHref,
  onSignedIn,
}: {
  institution: Institution;
  token: string;
  loginHref: string;
  onSignedIn: () => void;
}) {
  const reduce = useReducedMotion();
  const { verifyMagicLink } = useSession();
  const { strings: s } = usePortalLocale(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await verifyMagicLink(token);
        if (!cancelled) onSignedIn();
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && (err.status === 401 || err.status === 404)) {
          setError(s.loginMagicVerifyFailed);
        } else {
          setError(
            authErrorMessage(
              err,
              {
                errorOffline: s.errorOffline,
                errorTooManyRequests: s.errorTooManyRequests,
                errorServiceDown: s.errorServiceDown,
              },
              s.loginMagicVerifyError,
            ),
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    token,
    verifyMagicLink,
    onSignedIn,
    s.errorOffline,
    s.errorServiceDown,
    s.errorTooManyRequests,
    s.loginMagicVerifyError,
    s.loginMagicVerifyFailed,
  ]);

  return (
    <div className="portal-aurora bg-background text-foreground grid min-h-dvh place-items-center px-6">
      <motion.div
        initial={reduce ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: EASE_OUT }}
        className="border-border/70 bg-card/80 w-full max-w-md rounded-2xl border p-8 text-center shadow-lg backdrop-blur-xl"
      >
        <div className="mb-4 flex justify-center">
          <InstitutionMark vertical={institution.vertical} name={institution.name} size="md" />
        </div>
        {error ? (
          <>
            <p role="alert" className="text-destructive text-sm font-medium">
              {error}
            </p>
            <Button variant="outline" className="mt-6" asChild>
              <a href={loginHref}>{s.loginBackToSignIn}</a>
            </Button>
          </>
        ) : (
          <div className="text-muted-foreground flex items-center justify-center gap-2 text-sm">
            <Loader2 className="size-5 animate-spin" aria-hidden="true" />
            {s.loginMagicVerifyTitle}
          </div>
        )}
      </motion.div>
    </div>
  );
}
